import React, { useMemo } from "react";
import { t, Trans } from "@lingui/macro";
import { useWeb3React } from "@web3-react/core";
import useSWR from "swr";
import TooltipComponent from "components/Tooltip/Tooltip";
import { ethers } from "ethers";
import { formatDate } from "lib/dates";
import {
  arrayURLFetcher,
  BASIS_POINTS_DIVISOR,
  CAD_DECIMALS,
  CLP_DECIMALS,
  getPageTitle,
  USD_DECIMALS,
} from "lib/legacy";
import { useCadPrice, useTotalCadStaked, useTotalCadSupply } from "domain/legacy";

import { getContract } from "config/contracts";

import VaultV2 from "abis/VaultV2.json";
import ReaderV2 from "abis/ReaderV2.json";
import ClpManager from "abis/ClpManager.json";

import "./DashboardV2.css";
import SEO from "components/Common/SEO";
import { useFeesSummary, useTotalVolume, useVolumeInfo } from "domain/stats";
import { CANTO, getChainName } from "config/chains";
import { getServerUrl } from "config/backend";
import { contractFetcher } from "lib/contracts";
import { useInfoTokens } from "domain/tokens";
import { CLP_POOL_COLORS, getWhitelistedTokens } from "config/tokens";
import { bigNumberify, expandDecimals, formatAmount, numberWithCommas } from "lib/numbers";
import { useChainId } from "lib/chains";
import classes from "./DashboardStyles.module.scss";
import { Wrapper } from "../../components/Wrapper/Wrapper";
import { DashboardStatsCard } from "../../components/DashboardStatsCard/DashboardStatsCard";
import { uniqueId } from "lodash";
import { DashboardTokensCard } from "../../components/DashboardTokensCard/DashboardTokensCard";
import CadIcon from "../../img/CAD_logo.svg";
import ClpIcon from "../../img/clpIcon.png";
import { DashboardCompositionTable } from "../../components/DashboardCompositionTable/DashboardCompositionTable";
import { ReactComponent as InfoIcon } from "./assets/infoIcon.svg";
import PieChartComponent from "../../components/PieChart/PieChart";
import { percentOfValueFromValue } from "../../components/VestingCard/utils";

const ACTIVE_CHAIN_IDS = [CANTO];

const { AddressZero } = ethers.constants;

function getPositionStats(positionStats) {
  if (!positionStats || positionStats.length === 0) {
    return null;
  }
  return positionStats.reduce(
    (acc, cv, i) => {
      acc.totalLongPositionSizes = acc.totalLongPositionSizes.add(cv.totalLongPositionSizes);
      acc.totalShortPositionSizes = acc.totalShortPositionSizes.add(cv.totalShortPositionSizes);
      acc[ACTIVE_CHAIN_IDS[i]] = cv;
      return acc;
    },
    {
      totalLongPositionSizes: bigNumberify(0),
      totalShortPositionSizes: bigNumberify(0),
    }
  );
}

function getCurrentFeesUsd(tokenAddresses, fees, infoTokens) {
  if (!fees || !infoTokens) {
    return bigNumberify(0);
  }

  let currentFeesUsd = bigNumberify(0);

  for (let i = 0; i < tokenAddresses.length; i++) {
    const tokenAddress = tokenAddresses[i];
    const tokenInfo = infoTokens[tokenAddress];
    if (!tokenInfo || !tokenInfo.contractMinPrice) {
      continue;
    }

    const feeUsd = fees[i].mul(tokenInfo.contractMinPrice).div(expandDecimals(1, tokenInfo.decimals));
    currentFeesUsd = currentFeesUsd.add(feeUsd);
  }

  return currentFeesUsd;
}

export default function DashboardV2() {
  const { active, library } = useWeb3React();
  const { chainId } = useChainId();
  const totalVolume = useTotalVolume();
  const chainName = getChainName(chainId);

  const { data: positionStats } = useSWR(
    ACTIVE_CHAIN_IDS.map((chainId) => getServerUrl(chainId, "/api/position_stats")),
    {
      fetcher: arrayURLFetcher,
    }
  );

  let {
    total: totalCadSupply,
    liquidityCanto: liquidityCanto,
    liquidityEth: liquidityEth,
    totalUnlocked: totalUnlocked,
  } = useTotalCadSupply(chainId);

  const currentVolumeInfo = useVolumeInfo();

  const positionStatsInfo = getPositionStats(positionStats);

  function getWhitelistedTokenAddresses(chainId) {
    const whitelistedTokens = getWhitelistedTokens(chainId);
    return whitelistedTokens.map((token) => token.address);
  }

  const whitelistedTokens = getWhitelistedTokens(chainId);
  const tokenList = whitelistedTokens.filter((t) => !t.isWrapped);
  const visibleTokens = tokenList.filter((t) => !t.isTempHidden);

  const readerAddress = getContract(chainId, "Reader");
  const vaultAddress = getContract(chainId, "Vault");
  const clpManagerAddress = getContract(chainId, "ClpManager");

  const cadAddress = getContract(chainId, "CAD");
  const clpAddress = getContract(chainId, "CLP");
  const usdgAddress = getContract(chainId, "USDG");

  const tokensForSupplyQuery = [cadAddress, clpAddress, usdgAddress];

  const { data: aums } = useSWR([`Dashboard:getAums:${active}`, chainId, clpManagerAddress, "getAums"], {
    fetcher: contractFetcher(library, ClpManager),
  });

  const { data: totalSupplies } = useSWR(
    [`Dashboard:totalSupplies:${active}`, chainId, readerAddress, "getTokenBalancesWithSupplies", AddressZero],
    {
      fetcher: contractFetcher(library, ReaderV2, [tokensForSupplyQuery]),
    }
  );

  const { data: totalTokenWeights } = useSWR(
    [`ClpSwap:totalTokenWeights:${active}`, chainId, vaultAddress, "totalTokenWeights"],
    {
      fetcher: contractFetcher(library, VaultV2),
    }
  );

  const { infoTokens } = useInfoTokens(library, chainId, active, undefined, undefined);
  const { infoTokens: infoTokensCanto } = useInfoTokens(null, CANTO, active, undefined, undefined);

  const { data: currentFees } = useSWR(infoTokensCanto[AddressZero].contractMinPrice ? "Dashboard:currentFees" : null, {
    fetcher: async () => {
      const fees = [
        await contractFetcher(library, ReaderV2, [getWhitelistedTokenAddresses(CANTO)])(
          `Dashboard:fees:${CANTO}`,
          CANTO,
          getContract(CANTO, "Reader"),
          "getFees",
          getContract(CANTO, "Vault")
        ),
      ];

      const updatedVals = fees.reduce(
        (acc, cv, i) => {
          const feeUSD = getCurrentFeesUsd(getWhitelistedTokenAddresses(ACTIVE_CHAIN_IDS[i]), cv, infoTokensCanto);
          acc[ACTIVE_CHAIN_IDS[i]] = feeUSD;
          acc.total = acc.total.add(feeUSD);
          return acc;
        },
        { total: bigNumberify(0) }
      );

      return updatedVals;
    },
  });

  const { data: feesSummaryByChain } = useFeesSummary();
  const feesSummary = feesSummaryByChain[chainId];

  const shouldIncludeCurrrentFees =
    feesSummary?.lastUpdatedAt &&
    parseInt(Date.now() /* (divide by 1000 maybe? */) - feesSummary?.lastUpdatedAt > 60 * 60;

  const totalFees = ACTIVE_CHAIN_IDS.map((chainId) => {
    if (shouldIncludeCurrrentFees && currentFees && currentFees[chainId]) {
      return currentFees[chainId].div(expandDecimals(1, USD_DECIMALS)).add(feesSummaryByChain[chainId]?.totalFees || 0);
    }

    return feesSummaryByChain[chainId].totalFees || 0;
  })

    .map((v) => Math.round(v))
    .reduce(
      (acc, cv, i) => {
        acc[ACTIVE_CHAIN_IDS[i]] = cv;
        acc.total = acc.total + cv;
        return acc;
      },
      { total: 0 }
    );

  const weeklyFees = currentFees ? currentFees[chainId].div(expandDecimals(1, USD_DECIMALS)) : "...";

  const { cadPrice } = useCadPrice(chainId, { canto: chainId === CANTO ? library : undefined }, active);

  let { total: totalStakedCad } = useTotalCadStaked({
    canto: chainId === CANTO ? library : undefined,
  });

  let cadMarketCap;
  if (cadPrice && totalUnlocked) {
    cadMarketCap = cadPrice.mul(totalUnlocked).div(expandDecimals(1, CAD_DECIMALS));
  }

  // let fdv;
  // if (cadPrice && bigTotalSupply) {
  //   const bigTotalSupply = expandDecimals(250000000, 18);
  //   fdv = cadPrice.mul(bigTotalSupply).div(expandDecimals(1, CAD_DECIMALS));
  // }

  let stakedCadSupplyUsd;
  if (cadPrice && totalStakedCad) {
    stakedCadSupplyUsd = totalStakedCad.mul(cadPrice).div(expandDecimals(1, CAD_DECIMALS));
  }

  let cadSupplyUsd;
  if (cadPrice && totalCadSupply) {
    cadSupplyUsd = totalCadSupply.mul(cadPrice).div(expandDecimals(1, CAD_DECIMALS));
  }

  let cadSupplyUnlockedUsd;
  if (cadPrice && totalUnlocked) {
    cadSupplyUnlockedUsd = totalUnlocked.mul(cadPrice).div(expandDecimals(1, CAD_DECIMALS));
  }

  let aum;
  if (aums && aums.length > 0) {
    aum = aums[0].add(aums[1]).div(2);
  }

  let clpPrice;
  let clpSupply;
  let clpMarketCap;
  if (aum && totalSupplies && totalSupplies[3]) {
    clpSupply = totalSupplies[3];
    clpPrice =
      aum && aum.gt(0) && clpSupply.gt(0)
        ? aum.mul(expandDecimals(1, CLP_DECIMALS)).div(clpSupply)
        : expandDecimals(1, USD_DECIMALS);
    clpMarketCap = clpPrice.mul(clpSupply).div(expandDecimals(1, CLP_DECIMALS));
  }

  let tvl;

  if (clpMarketCap && cadPrice && totalStakedCad) {
    tvl = clpMarketCap.add(cadPrice.mul(totalStakedCad).div(expandDecimals(1, CAD_DECIMALS)));
  }

  let adjustedUsdgSupply = bigNumberify(0);

  for (let i = 0; i < tokenList.length; i++) {
    const token = tokenList[i];
    const tokenInfo = infoTokens[token.address];
    if (tokenInfo && tokenInfo.usdgAmount) {
      adjustedUsdgSupply = adjustedUsdgSupply.add(tokenInfo.usdgAmount);
    }
  }

  const totalStakedCadPercent = percentOfValueFromValue(
    formatAmount(stakedCadSupplyUsd, USD_DECIMALS, 0, false),
    formatAmount(cadSupplyUnlockedUsd, USD_DECIMALS, 0, false)
  );

  const stakedCadChart = [
    {
      name: "unstaked",
      color: "#9FA4C4",
      value: 100 - totalStakedCadPercent - liquidityCanto - liquidityEth,
    },
    {
      name: t`total staked`,
      color: "#1DB849",
      value: totalStakedCadPercent,
    },
    {
      name: t`in ETH liquidity`,
      color: "#48cbd9",
      value: liquidityEth,
    },
    {
      name: t`in CANTO liquidity`,
      color: "#14fc99",
      value: liquidityCanto,
    },
  ];

  const totalStakedClp = percentOfValueFromValue(
    formatAmount(clpMarketCap, USD_DECIMALS, 0, false),
    formatAmount(clpSupply, CAD_DECIMALS, 0, false)
  );

  const stakedClpChart = useMemo(() => {
    if (!totalStakedClp) return [];
    return [
      {
        name: "",
        value: 100 - totalStakedClp,
      },
      {
        name: t`Total staked`,
        value: `$${Math.round(totalStakedClp)}`,
      },
    ];
  }, [totalStakedClp]);

  let stableClp = 0;
  let totalClp = 0;

  let clpPool = tokenList.map((token) => {
    const defaultColor = "#4F1271";
    const tokenInfo = infoTokens[token.address];
    if (tokenInfo.usdgAmount && adjustedUsdgSupply && adjustedUsdgSupply.gt(0)) {
      const currentWeightBps = tokenInfo.usdgAmount.mul(BASIS_POINTS_DIVISOR).div(adjustedUsdgSupply);
      if (tokenInfo.isStable) {
        stableClp += parseFloat(`${formatAmount(currentWeightBps, 2, 2, false)}`);
      }
      totalClp += parseFloat(`${formatAmount(currentWeightBps, 2, 2, false)}`);
      return {
        name: token.symbol,
        color: CLP_POOL_COLORS[token.symbol] || defaultColor,
        value: parseFloat(`${formatAmount(currentWeightBps, 2, 2, false)}`),
      };
    }
    return null;
  });

  let stablePercentage = totalClp > 0 ? ((stableClp * 100) / totalClp).toFixed(2) : "0.0";

  const statsCardsProps = [
    {
      headerItems: [
        {
          title: "AUM",
          tooltip: (
            <TooltipComponent
              handle={
                <div className={classes.infoIconWrapper}>
                  <InfoIcon />
                </div>
              }
              position="right-bottom"
              renderContent={() => (
                <span>{t`Assets Under Management: CAD staked (All chains) + CLP pool (${chainName}).`}</span>
              )}
            />
          ),
          ammount: `$${formatAmount(tvl, USD_DECIMALS, 0, true)}`,
        },
        {
          title: "CLP Pool",
          tooltip: (
            <TooltipComponent
              handle={
                <div className={classes.infoIconWrapper}>
                  <InfoIcon />
                </div>
              }
              position="right-bottom"
              renderContent={() => (
                <Trans>
                  <p>Total value of tokens in CLP pool ({chainName}).</p>
                  <p>Other websites may show a higher value as they add positions' collaterals to the CLP pool.</p>
                </Trans>
              )}
            />
          ),
          ammount: `$${formatAmount(aum, USD_DECIMALS, 0, true)}`,
        },
      ],
      statsItems: [
        {
          title: "Total open positions",
          content: positionStatsInfo?.[chainId]?.totalActivePositions,
        },
        {
          title: "Long positions",
          content: `$${formatAmount(positionStatsInfo?.[chainId]?.totalLongPositionSizes, USD_DECIMALS, 0, true)}`,
        },
        {
          title: "Short positions",
          content: `$${formatAmount(positionStatsInfo?.[chainId]?.totalShortPositionSizes, USD_DECIMALS, 0, true)}`,
        },
      ],
    },
    {
      headerItems: [
        { 
          title: "Total volume",
          tooltip: (
            <TooltipComponent
              handle={
                <div className={classes.infoIconWrapper}>
                  <InfoIcon />
                </div>
              }
              position="right-bottom"
              renderContent={() => (
                <Trans>
                  <p>Total volume of all trades on Cadence Perpetuals.</p>
                </Trans>
              )}
            />
          ),
          ammount: `$${formatAmount(totalVolume ? totalVolume[0][7701] : undefined, USD_DECIMALS, 0, true)}`,
        },
        // {
        //   title: "24h Volume",
        //   ammount: `$${formatAmount(
        //     currentVolumeInfo ? currentVolumeInfo[0][7701] : undefined,
        //     USD_DECIMALS,
        //     0,
        //     true
        //   )}`,
        // },
      ],
      statsItems: [
        // {
        //   title: "Fees since " + (feesSummary.lastUpdatedAt ? formatDate(feesSummary.lastUpdatedAt) : "..."),
        //   content: `$${numberWithCommas(weeklyFees)}`,
        // },
        {
          title: "Total Fees",
          content: `$${numberWithCommas(totalFees?.[chainId])}`,
        },
        {
          title: "Total Users",
          content: positionStatsInfo?.[chainId]?.totalPlatformUsers + 710,
          // content: 676,
        },
      ],
    },
  ];

  const tokensStatsProps = [
    {
      icon: CadIcon,
      title: "CAD",
      price: formatAmount(cadPrice, USD_DECIMALS, 2, true),
      marketCap: formatAmount(cadMarketCap, USD_DECIMALS, 0, true),
      //fdv: formatAmount(fdv, USD_DECIMALS, 0, true),
      statsContent: [
        {
          title: "Circulating Supply",
          content: totalUnlocked ? formatAmount(totalUnlocked, CAD_DECIMALS, 0, true) : "",
        },
        {
          title: "Total staked",
          content: stakedCadSupplyUsd ? `$${formatAmount(stakedCadSupplyUsd, USD_DECIMALS, 0, true)}` : "",
        },
      ],
      graph: <PieChartComponent data={stakedCadChart} color={"#1FED88"} />,
    },
    {
      icon: ClpIcon,
      title: "CLP",
      price: formatAmount(clpPrice, USD_DECIMALS, 2, true),
      marketCap: formatAmount(clpMarketCap, USD_DECIMALS, 0, true),
      statsContent: [
        {
          title: "Supply",
          content: formatAmount(clpSupply, CLP_DECIMALS, 0, true),
        },
        {
          title: "Total staked",
          content: `$${formatAmount(clpMarketCap, USD_DECIMALS, 0, true)}`,
        },
        {
          title: "Stablecoin percentage",
          content: `${stablePercentage}%`,
        },
      ],
      graph: clpPool && tokenList ? <PieChartComponent data={clpPool} color={"#AC42FF"} /> : <></>,
    },
  ];

  return (
    <SEO title={getPageTitle("Dashboard")}>
      <Wrapper>
        <section className={classes.stats}>
          <div className={classes.titleWrapper}>
            <h2 className={classes.sectionTitle}>
              <Trans>Stats</Trans>
            </h2>
          </div>
          <div className={classes.statsContentWrapper}>
            {statsCardsProps.map((card) => (
              <DashboardStatsCard key={uniqueId(card.headerItems)} {...card} />
            ))}
          </div>
        </section>
        <section className={classes.stats}>
          <div className={classes.titleWrapper}>
            <h2 className={classes.sectionTitle}>
              <Trans>Tokens</Trans>
            </h2>
            <span className={classes.subtitle}>Platform and CLP index tokens.</span>
          </div>
          <div className={classes.statsContentWrapper}>
            {tokensStatsProps.map((card) => (
              <DashboardTokensCard key={uniqueId(card.title)} {...card} />
            ))}
          </div>
        </section>
        <section className={classes.table}>
          <DashboardCompositionTable
            infoTokens={infoTokens}
            visibleTokens={visibleTokens}
            adjustedUsdgSupply={adjustedUsdgSupply}
            totalTokenWeights={totalTokenWeights}
          />
        </section>
      </Wrapper>
    </SEO>
  );
}
